import { indexOf, omit, xor } from 'lodash';

import { Action } from '../actions';
import {
    CHANGE_BANNER_IMAGE_CREATIVE_UPLOADING_LINK,
    CLOSE_BANNER_IMAGE_CANVAS,
    SHOW_BANNER_IMAGE_CANVAS,
    UPLOAD_AD_BANNER_IMAGES,
    UPLOAD_BANNER_IMAGE_CREATIVE_BY_LINK
} from '../actions/banners-edit/banner-image';
import {
    ADD_BANNER_IMAGE,
    ADD_IMAGES_TO_NEW_BANNERS,
    CANCEL_UPLOAD_BANNER_IMAGE,
    CANCEL_UPLOAD_IMAGE_CREATIVE,
    CLEAR_IMAGE_EDITOR_DATA,
    CLOSE_IMAGE_CROP,
    CLOSE_IMAGES_GALLERY,
    ON_ACCEPT_BULK_IMAGE_REPLACEMENT,
    REMOVE_BANNER_IMAGE,
    RESET_BANNER_IMAGE_CREATIVE_MODE,
    RESET_BANNER_IMAGE_UPLOAD_ERROR,
    SELECT_IMAGES_IN_GALLERY,
    SELECT_ONE_IMAGE_IN_GALLERY,
    SHOW_BANNER_IMAGE_CREATIVE_UPLOAD_ERROR,
    SHOW_BANNER_IMAGE_LINK_INPUT,
    SHOW_BANNER_IMAGE_UPLOAD_ERROR,
    SHOW_IMAGE_GALLERY,
    SHOW_IMAGE_GALLERY_ERROR,
    UPDATE_BANNER_IMAGE,
    UPDATE_BANNER_IMAGE_BY_GALLERY,
    UPDATE_BANNER_IMAGE_BY_LINK,
    UPDATE_BANNER_IMAGE_CREATIVE,
    UPDATE_IMAGE_EDITOR,
    UPDATE_IMAGES_FOR_GALLERY_EDIT,
    UpdateBannerImage,
    UPLOAD_BANNER_IMAGE,
    UPLOAD_BANNER_IMAGE_BY_LINK
} from '../actions/banners-edit/banner-text-image';
import {
    ACCEPT_MASS_ACTIONS_BULK_IMAGE_CREATIVE_REPLACEMENT_EDITOR,
    CLOSE_MASS_ACTIONS_BULK_IMAGE_CREATIVE_REPLACEMENT_EDITOR
} from '../actions/banners-edit/banners-edit-mass-actions';
import {
    ADD_NEW_TEXT_BANNERS,
    AddNewTextBannersAction,
    CLEAR_BANNERS,
    COPY_BANNER,
    COPY_PREV_BANNER,
    CopyBanner,
    CopyPrevBanner,
    CREATE_NEW_MC_BANNER,
    CREATE_NEW_TEXT_BANNER,
    CreateNewMcBannerAction,
    CreateNewTextBannerAction,
    DELETE_BANNERS,
    ON_COPY_PREV_EDITED_BANNER,
    OnCopyPrevEditedBanners,
    RESET_BANNERS_EDIT,
    SAVE_BANNERS_SUCCESS,
    SaveBannersSuccess,
    UPDATE_BANNER_EDIT_IMAGE_FORMATS,
    UpdateBannerEditImageFormatsAction
} from '../actions/bannersEdit';
import { ADD_INTERNAL_BANNER_IMAGE } from '../actions/imageEditor/internal/addInternalBannerImage';
import { CANCEL_UPLOAD_INTERNAL_BANNER_IMAGE } from '../actions/imageEditor/internal/cancelUploadInternalBannerImage';
import { CLOSE_IMAGES_INTERNAL_GALLERY } from '../actions/imageEditor/internal/closeImagesInternalGallery';
import { DUPLICATE_INTERNAL_BANNER_IMAGE } from '../actions/imageEditor/internal/duplicateInternalBannerImage';
import { REMOVE_INTERNAL_BANNER_IMAGE } from '../actions/imageEditor/internal/removeInternalBannerImage';
import { RESET_INTERNAL_BANNER_IMAGE_UPLOAD_ERROR } from '../actions/imageEditor/internal/resetInternalBannerImageUploadError';
import { SELECT_ONE_IMAGE_INTERNAL_IN_GALLERY } from '../actions/imageEditor/internal/selectOneImageInternalInGallery';
import { SHOW_IMAGE_INTERNAL_GALLERY } from '../actions/imageEditor/internal/showImageInternalGallery';
import { SHOW_IMAGE_INTERNAL_GALLERY_ERROR } from '../actions/imageEditor/internal/showImageInternalGalleryError';
import { SHOW_INTERNAL_BANNER_IMAGE_LINK_INPUT } from '../actions/imageEditor/internal/showInternalBannerImageLinkInput';
import { SHOW_INTERNAL_BANNER_IMAGE_UPLOAD_ERROR } from '../actions/imageEditor/internal/showInternalBannerImageUploadError';
import { UPDATE_IMAGES_INTERNAL_FOR_GALLERY_EDIT } from '../actions/imageEditor/internal/updateImagesInternalForGalleryEdit';
import { UPDATE_INTERNAL_BANNER_IMAGE } from '../actions/imageEditor/internal/updateInternalBannerImage';
import { UPDATE_INTERNAL_BANNER_IMAGE_BY_GALLERY } from '../actions/imageEditor/internal/updateInternalBannerImageByGallery';
import { UPLOAD_INTERNAL_BANNER_IMAGE } from '../actions/imageEditor/internal/uploadInternalBannerImage';
import { UPLOAD_INTERNAL_BANNER_IMAGE_BY_LINK } from '../actions/imageEditor/internal/uploadInternalBannerImageByLink';
import { BulkReplacementRadio } from '../components/BulkReplacementModeChooser/BulkReplacementModeChooser';
import { selectImageCreativeReplacement } from '../selectors/bannerEdit/massActions/massActions';
import {
    imageGalleryTextErrorMapper,
    imageUploadTextErrorMapper
} from '../service/BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import { getEmptyBannerImage } from '../service/BannerHelpers/BannerHelpers';
import { isBannerTextMassActionsAvailable } from '../service/BannersEditMassActionsHelpers/BannersEditMassActionsHelpers';
import { getUploadingImagesHash } from '../service/ImageHelpers/ImageHelpers';
import {
    BannerId,
    BannersToClear,
    ImageEditorId,
    ImageItem,
    ImageOrCreativeUploadingMode,
    SaveBannersResultData
} from '../typings/banners';
import { EditedBannerImage, getBannerWithImage, MassActionId } from '../typings/bannersEditState';
import { ValidationErrorData, ValidationResultItem } from '../typings/common';
import { BannerImageForEdit } from '../typings/domain';
import { ImageEditorsState, ImageEditorState, State } from '../typings/state';

import { ImageInitialState } from './bannersEdit';
import { addInternalBannerImage } from './imageEditor/internal/addInternalBannerImage';
import { cancelUploadInternalBannerImage } from './imageEditor/internal/cancelUploadInternalBannerImage';
import { closeImagesInternalGallery } from './imageEditor/internal/closeImagesInternalGallery';
import { duplicateInternalBannerImage } from './imageEditor/internal/duplicateInternalBannerImage';
import { removeInternalBannerImage } from './imageEditor/internal/removeInternalBannerImage';
import { resetInternalBannerImageUploadError } from './imageEditor/internal/resetInternalBannerImageUploadError';
import { selectOneImageInternalInGallery } from './imageEditor/internal/selectOneImageInternalInGallery';
import { showImageInternalGallery } from './imageEditor/internal/showImageInternalGallery';
import { showImageInternalGalleryError } from './imageEditor/internal/showImageInternalGalleryError';
import { showInternalBannerImageLinkInput } from './imageEditor/internal/showInternalBannerImageLinkInput';
import { showInternalBannerImageUploadError } from './imageEditor/internal/showInternalBannerImageUploadError';
import { updateImagesInternalForGalleryEdit } from './imageEditor/internal/updateImagesInternalForGalleryEdit';
import { updateInternalBannerImage } from './imageEditor/internal/updateInternalBannerImage';
import { updateInternalBannerImageByGallery } from './imageEditor/internal/updateInternalBannerImageByGallery';
import { uploadInternalBannerImage } from './imageEditor/internal/uploadInternalBannerImage';
import { uploadInternalBannerImageByLink } from './imageEditor/internal/uploadInternalBannerImageByLink';

function getImageEditorInitialState(): ImageEditorsState {
    return {
        [MassActionId.bannersEdit]: getImageEditorForMassActionsInitialState()
    };
}

export function getImageEditorForMassActionsInitialState(): ImageEditorState {
    return {
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        imageUploadingLink: '',
        image: ImageInitialState,
        isImageCanvasOpen: false,
        isCropOpen: false,
        isNew: true,
        canvasPageType: undefined
    };
}

export function getBannerImageUploadedState(image: BannerImageForEdit | ImageItem): EditedBannerImage {
    return {
        imageUrl: image.url,
        uploadProgress: 0,
        data: image,
        mode: ImageOrCreativeUploadingMode.UPLOADED,
        displayName: image.name,
        fixedSize: {
            width: image.width,
            height: image.height
        }
    };
}

export function updateBannerImage(state: ImageEditorsState, action: UpdateBannerImage): ImageEditorsState {
    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            image: {
                ...state[action.bannerId].image,
                ...getBannerImageUploadedState(action.image)
            },
            imageUploadingLink: ''
        }
    };
}

function updateBannerEditImageFormatsReducer(
    state: ImageEditorsState,
    action: UpdateBannerEditImageFormatsAction,
    fullState: State
): ImageEditorsState {
    const banner = getBannerWithImage(fullState.editBanners.banners[action.bannerId]);

    if (!banner) {
        return state;
    }
    const data = state[action.bannerId].image.data;

    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            image: {
                ...state[action.bannerId].image,
                data:
                    data !== undefined
                        ? {
                              ...data,
                              formats: action.payload
                          }
                        : undefined
            }
        }
    };
}

const removeBannerImages = (state: ImageEditorsState, bannerIds: BannerId[]): ImageEditorsState => {
    const result = bannerIds.reduce((acc, id) => {
        return {
            [id]: {
                ...state[id],
                image: {
                    ...ImageInitialState,
                    fixedSize: state[id].image.fixedSize
                }
            }
        };
    }, {});

    return {
        ...state,
        ...result
    };
};

const resetBannerImage = (state: ImageEditorsState, bannersToClear: BannersToClear): ImageEditorsState => {
    const result = Object.keys(state).reduce(
        (acc: ImageEditorsState, bannerId): ImageEditorsState => {
            let nextImageEditorState = state[bannerId] as ImageEditorState;
            const bannerToClearOptions = bannersToClear[bannerId];

            if (bannerToClearOptions) {
                let imageData;
                let fixedSize;
                if (nextImageEditorState.image) {
                    imageData = nextImageEditorState.image.data;
                    fixedSize = nextImageEditorState.image.fixedSize;
                }

                if (bannerToClearOptions.keepBannerImageSize) {
                    const clearedImageState = getImageEditorForMassActionsInitialState();

                    nextImageEditorState = {
                        ...clearedImageState,
                        image: {
                            ...clearedImageState.image,
                            fixedSize,
                            data:
                                imageData !== undefined
                                    ? {
                                          name: '',
                                          url: '',
                                          type: imageData.type,
                                          width: imageData.width,
                                          height: imageData.height
                                      }
                                    : undefined
                        }
                    };
                } else {
                    nextImageEditorState = getImageEditorForMassActionsInitialState();
                }
            }

            return { ...acc, [bannerId]: nextImageEditorState };
        },
        {} as ImageEditorsState
    );

    return result;
};

const deleteBannerImage = (state: ImageEditorsState, bannerIds: BannerId[], fullState: State): ImageEditorsState => {
    const bannersIdsForDelete = bannerIds.filter(id => fullState.editBanners.banners[id].markedForDeletion !== true);

    const result = omit(state, bannersIdsForDelete);

    return result;
};

function replaceOrDeleteImages(state: ImageEditorsState, fullState: State): ImageEditorsState {
    const { banners } = fullState.editBanners;
    const { replaceOrDeleteToggle } = fullState.bannersEditMassActions.bannersEdit.data;
    const selectedImage = state[MassActionId.bannersEdit].image;
    const updatedImageEditor = Object.keys(banners).reduce((acc, bannerId) => {
        acc[bannerId] = {
            ...state[bannerId]
        };

        if (isBannerTextMassActionsAvailable(banners[bannerId])) {
            if (replaceOrDeleteToggle === BulkReplacementRadio.REPLACE) {
                acc[bannerId].image = selectedImage;
            } else {
                acc[bannerId].image = {
                    fixedSize: state[bannerId].image.fixedSize,
                    ...ImageInitialState
                };
            }
        }

        return acc;
    }, {});

    return {
        ...state,
        ...updatedImageEditor,
        [MassActionId.bannersEdit]: {
            ...getImageEditorForMassActionsInitialState()
        }
    };
}

function addNewTextBanners(state: ImageEditorsState, action: AddNewTextBannersAction): ImageEditorsState {
    const result: Record<ImageEditorId, ImageEditorState> = action.banners.reduce(
        (data: Record<ImageEditorId, ImageEditorState>, banner) => {
            data[banner.bannerData.id] = {
                image: getEmptyBannerImage(),
                isImageCanvasOpen: false,
                imageGallery: {
                    images: undefined,
                    isImageGalleryOpen: false,
                    selectedImageHashFromGallery: []
                },
                isNew: true,
                imageUploadingLink: ''
            };

            return data;
        },
        {}
    );

    return {
        ...state,
        ...result
    };
}

function copyBannerImageInfoReducer(
    state: ImageEditorsState,
    action: CopyBanner | CopyPrevBanner | OnCopyPrevEditedBanners
): ImageEditorsState {
    const imageInfo = { ...state[action.bannerId] };

    if (!imageInfo) {
        return state;
    }

    return {
        ...state,
        [action.newBannerId]: {
            ...imageInfo,
            image: {
                ...imageInfo.image,
                errors: undefined
            }
        }
    };
}

function createNewImagesTextBanners(
    state: ImageEditorsState,
    action: CreateNewTextBannerAction | CreateNewMcBannerAction
): ImageEditorsState {
    return {
        ...state,
        [action.bannerId]: {
            image: getEmptyBannerImage(),
            isImageCanvasOpen: false,
            imageGallery: {
                images: undefined,
                isImageGalleryOpen: false,
                selectedImageHashFromGallery: []
            },
            imageUploadingLink: ''
        }
    };
}

const setSavedImages = (state: ImageEditorsState, action: SaveBannersSuccess): ImageEditorsState => {
    const { added, mcBannerAdded } = action.result;
    let imageState = state;

    [added, mcBannerAdded].forEach((addedBanners: SaveBannersResultData) => {
        if (!addedBanners.banners.length) {
            return;
        }

        addedBanners.banners.forEach((addedBanner, index) => {
            if (!addedBanner) {
                return;
            }
            const oldId = addedBanners.ids[index];
            const newId = String(addedBanner.id);

            imageState = {
                ...omit(imageState, oldId),
                [newId]: {
                    ...imageState[oldId]
                }
            };
        });

        state = imageState;
    });

    return state;
};

export function imageEditorReducer(
    state: ImageEditorsState = getImageEditorInitialState(),
    action: Action,
    fullState: State
): ImageEditorsState {
    switch (action.type) {
        case CLOSE_IMAGE_CROP:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    isCropOpen: false
                }
            };
        case RESET_BANNERS_EDIT:
            return getImageEditorInitialState();
        case ADD_NEW_TEXT_BANNERS:
            return addNewTextBanners(state, action);
        case ON_COPY_PREV_EDITED_BANNER:
        case COPY_BANNER:
        case COPY_PREV_BANNER:
            return copyBannerImageInfoReducer(state, action);
        case UPLOAD_BANNER_IMAGE:
        case UPLOAD_AD_BANNER_IMAGES:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: action.isLocalUploading
                            ? ImageOrCreativeUploadingMode.UPLOADING_LOCAL
                            : ImageOrCreativeUploadingMode.UPLOADING,
                        uploadingData: getUploadingImagesHash(action.filesToUpload),
                        errors: undefined
                    }
                }
            };

        case UPDATE_IMAGE_EDITOR:
            return {
                ...action.data,
                ...getImageEditorInitialState()
            };

        case CLEAR_IMAGE_EDITOR_DATA:
            return {
                ...state,
                [action.bannerId]: getImageEditorForMassActionsInitialState()
            };

        case SHOW_BANNER_IMAGE_LINK_INPUT:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: ImageOrCreativeUploadingMode.ENTER_LINK,
                        errors: undefined
                    }
                }
            };

        case CANCEL_UPLOAD_BANNER_IMAGE:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: ImageOrCreativeUploadingMode.EMPTY,
                        displayName: '',
                        uploadingData: undefined,
                        errors: undefined
                    }
                }
            };

        case CANCEL_UPLOAD_IMAGE_CREATIVE:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: ImageOrCreativeUploadingMode.EMPTY,
                        errors: undefined
                    }
                }
            };

        case SHOW_BANNER_IMAGE_UPLOAD_ERROR:
            if (action.isMassAction) {
                return {
                    ...state,
                    [action.bannerId]: {
                        ...state[action.bannerId],
                        image: {
                            ...state[action.bannerId].image,
                            errors: action.errors.map(error => {
                                return {
                                    code: error.code,
                                    text: imageUploadTextErrorMapper(error)
                                };
                            })
                        }
                    }
                };
            }

            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: action.isUploadByLink
                            ? ImageOrCreativeUploadingMode.ENTER_LINK
                            : ImageOrCreativeUploadingMode.ERROR,
                        displayName: '',
                        uploadingData: undefined,
                        uploadProgress: 0,
                        errors: action.errors.map(
                            (error: ValidationResultItem): ValidationErrorData => ({
                                code: error.code,
                                text: imageUploadTextErrorMapper(error)
                            })
                        )
                    },
                    isImageCanvasOpen: false
                }
            };

        case RESET_BANNER_IMAGE_CREATIVE_MODE:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: ImageOrCreativeUploadingMode.EMPTY
                    }
                }
            };

        case SHOW_BANNER_IMAGE_CREATIVE_UPLOAD_ERROR:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: action.isUploadByLink
                            ? ImageOrCreativeUploadingMode.ENTER_LINK
                            : ImageOrCreativeUploadingMode.ERROR,
                        uploadProgress: 0,
                        errors: action.errors.map(
                            (error: ValidationResultItem): ValidationErrorData => ({
                                code: error.code,
                                text: imageUploadTextErrorMapper(error)
                            })
                        )
                    },
                    isImageCanvasOpen: false
                }
            };

        case CHANGE_BANNER_IMAGE_CREATIVE_UPLOADING_LINK:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        errors: undefined
                    },
                    imageUploadingLink: action.link
                }
            };

        case RESET_BANNER_IMAGE_UPLOAD_ERROR:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        fixedSize: state[action.bannerId].image.fixedSize,
                        mode:
                            state[action.bannerId].image.mode === ImageOrCreativeUploadingMode.ENTER_LINK
                                ? ImageOrCreativeUploadingMode.ENTER_LINK
                                : ImageInitialState.mode,
                        errors: undefined
                    }
                }
            };

        case UPDATE_BANNER_IMAGE:
            return updateBannerImage(state, action);

        case ADD_BANNER_IMAGE:
            return {
                ...state,
                ...action.data
            };

        case ADD_IMAGES_TO_NEW_BANNERS: {
            const bannerIds = Object.keys(fullState.editBanners.banners);
            const newBannerIds = bannerIds.slice(bannerIds.length - action.newImages.length);
            const newBannerImages = newBannerIds.reduce((acc: ImageEditorsState, bannerId, index) => {
                acc[bannerId] = {
                    ...getImageEditorForMassActionsInitialState(),
                    image: getBannerImageUploadedState(action.newImages[index])
                };

                return acc;
            }, {});

            return {
                ...state,
                ...newBannerImages
            };
        }

        case UPLOAD_BANNER_IMAGE_BY_LINK:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        uploadProgress: 1,
                        mode: ImageOrCreativeUploadingMode.UPLOADING,
                        errors: undefined
                    }
                }
            };

        case UPLOAD_BANNER_IMAGE_CREATIVE_BY_LINK:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        uploadProgress: 1,
                        mode: ImageOrCreativeUploadingMode.UPLOADING,
                        errors: undefined
                    }
                }
            };

        case UPDATE_BANNER_IMAGE_BY_LINK:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        imageLinkToCrop: action.link,
                        uploadProgress: 0,
                        mode: ImageOrCreativeUploadingMode.EMPTY,
                        displayNameToCrop: action.name
                    },
                    isCropOpen: action.isCropNeeded
                }
            };

        case UPDATE_BANNER_EDIT_IMAGE_FORMATS:
            return updateBannerEditImageFormatsReducer(state, action, fullState);

        case REMOVE_BANNER_IMAGE:
            return removeBannerImages(state, [action.bannerId]);

        case UPDATE_BANNER_IMAGE_BY_GALLERY:
            const { images } = state[action.bannerId].imageGallery;
            const image = images !== undefined ? images.find(item => item.hash === action.imagesHash[0]) : undefined;

            return image !== undefined
                ? {
                      ...state,
                      [action.bannerId]: {
                          ...state[action.bannerId],
                          imageGallery: {
                              ...state[action.bannerId].imageGallery,
                              isImageGalleryOpen: false,
                              selectedImageHashFromGallery: [],
                              errors: undefined
                          },
                          image: {
                              ...state[action.bannerId].image,
                              imageUrl: image.url,
                              uploadProgress: 0,
                              data: {
                                  ...image,
                                  type: action.bannerType
                              },
                              mode: ImageOrCreativeUploadingMode.UPLOADED,
                              displayName: image.name,
                              fixedSize: {
                                  width: image.width,
                                  height: image.height
                              },
                              errors: undefined
                          }
                      }
                  }
                : state;

        case SELECT_ONE_IMAGE_IN_GALLERY:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        ...state[action.bannerId].imageGallery,
                        selectedImageHashFromGallery: [action.imageHash]
                    }
                }
            };

        // xor здесь используется для исключения элемента из массива, если чекнули имеющийся в массиве выбранных элементов элемент
        // пример - были выбран [1, 2, 3], хотим отчекнуть 1, получается _xor([1, 2, 3], [1]) = [2, 3]
        case SELECT_IMAGES_IN_GALLERY:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        ...state[action.bannerId].imageGallery,
                        selectedImageHashFromGallery:
                            indexOf(
                                state[action.bannerId].imageGallery.selectedImageHashFromGallery,
                                action.imageHash
                            ) !== -1
                                ? xor(state[action.bannerId].imageGallery.selectedImageHashFromGallery, [
                                      action.imageHash
                                  ])
                                : [
                                      ...state[action.bannerId].imageGallery.selectedImageHashFromGallery,
                                      action.imageHash
                                  ]
                    }
                }
            };

        case SHOW_IMAGE_GALLERY:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        ...state[action.bannerId].imageGallery,
                        isImageGalleryOpen: true,
                        selectedImageHashFromGallery: []
                    }
                }
            };

        case UPDATE_IMAGES_FOR_GALLERY_EDIT:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        ...state[action.bannerId].imageGallery,
                        images: action.images
                    }
                }
            };

        case CLOSE_IMAGES_GALLERY:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        images: undefined,
                        isImageGalleryOpen: false,
                        selectedImageHashFromGallery: [],
                        errors: undefined
                    }
                }
            };

        case SHOW_IMAGE_GALLERY_ERROR:
            const errors = action.errors.map(
                (error: ValidationResultItem): ValidationErrorData => ({
                    code: error.code,
                    path: error.path,
                    text: imageGalleryTextErrorMapper(error)
                })
            );

            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        ...state[action.bannerId].imageGallery,
                        errors
                    }
                }
            };

        case UPDATE_BANNER_IMAGE_CREATIVE:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        imageUrl: action.image.url,
                        uploadProgress: 0,
                        data: action.image,
                        mode: ImageOrCreativeUploadingMode.UPLOADED,
                        displayName: action.image.name,
                        fixedSize: {
                            height: action.image.height,
                            width: action.image.width,
                            isAdaptive: action.image.isAdaptive
                        },
                        errors: undefined
                    },
                    isImageCanvasOpen: false
                }
            };

        case SHOW_BANNER_IMAGE_CANVAS:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    isImageCanvasOpen: true,
                    canvasPageType: action.canvasType
                }
            };

        case CLOSE_BANNER_IMAGE_CANVAS:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    isImageCanvasOpen: false
                }
            };

        case ON_ACCEPT_BULK_IMAGE_REPLACEMENT:
            return replaceOrDeleteImages(state, fullState);
        case ACCEPT_MASS_ACTIONS_BULK_IMAGE_CREATIVE_REPLACEMENT_EDITOR:
            return {
                ...selectImageCreativeReplacement(fullState),
                [MassActionId.bannersEdit]: getImageEditorForMassActionsInitialState()
            };
        case CLEAR_BANNERS:
            return resetBannerImage(state, action.bannersToClear);
        case DELETE_BANNERS:
            return deleteBannerImage(state, action.bannersIds, fullState);
        case CLOSE_MASS_ACTIONS_BULK_IMAGE_CREATIVE_REPLACEMENT_EDITOR:
            return {
                ...state,
                [action.massActionId]: getImageEditorForMassActionsInitialState()
            };
        case CREATE_NEW_TEXT_BANNER:
        case CREATE_NEW_MC_BANNER:
            return createNewImagesTextBanners(state, action);
        case SAVE_BANNERS_SUCCESS:
            return setSavedImages(state, action);

        case DUPLICATE_INTERNAL_BANNER_IMAGE:
            return duplicateInternalBannerImage(state, action);

        case UPLOAD_INTERNAL_BANNER_IMAGE:
            return uploadInternalBannerImage(state, action);

        case SHOW_INTERNAL_BANNER_IMAGE_LINK_INPUT:
            return showInternalBannerImageLinkInput(state, action);

        case CANCEL_UPLOAD_INTERNAL_BANNER_IMAGE:
            return cancelUploadInternalBannerImage(state, action);

        case SHOW_INTERNAL_BANNER_IMAGE_UPLOAD_ERROR:
            return showInternalBannerImageUploadError(state, action);

        case RESET_INTERNAL_BANNER_IMAGE_UPLOAD_ERROR:
            return resetInternalBannerImageUploadError(state, action);

        case UPDATE_INTERNAL_BANNER_IMAGE:
            return updateInternalBannerImage(state, action);

        case UPLOAD_INTERNAL_BANNER_IMAGE_BY_LINK:
            return uploadInternalBannerImageByLink(state, action);

        case REMOVE_INTERNAL_BANNER_IMAGE:
            return removeInternalBannerImage(state, action);

        case UPDATE_INTERNAL_BANNER_IMAGE_BY_GALLERY:
            return updateInternalBannerImageByGallery(state, action);

        case SELECT_ONE_IMAGE_INTERNAL_IN_GALLERY:
            return selectOneImageInternalInGallery(state, action);

        case SHOW_IMAGE_INTERNAL_GALLERY:
            return showImageInternalGallery(state, action);

        case UPDATE_IMAGES_INTERNAL_FOR_GALLERY_EDIT:
            return updateImagesInternalForGalleryEdit(state, action);

        case CLOSE_IMAGES_INTERNAL_GALLERY:
            return closeImagesInternalGallery(state, action);

        case SHOW_IMAGE_INTERNAL_GALLERY_ERROR:
            return showImageInternalGalleryError(state, action);

        case ADD_INTERNAL_BANNER_IMAGE:
            return addInternalBannerImage(state, action);

        default:
            return state;
    }
}
